import {
  OmniElement,
  OmniStyleElement,
  OmniIconElement,
  html,
  css,
  nothing,
} from 'omni-ui';

OmniStyleElement.register();
OmniIconElement.register();

/**
 * Wraps an input field (<input>, <textarea>, or <campaign-wysiwyg>) to make it
 * "assist" enabled. Shows an omni-assist icon when the field is focused. When
 * clicked the prompt is executed and the result populated into the input field.
 *
 * @extends OmniElement
 */
export class CampaignAssistField extends OmniElement {
  constructor() {
    super();
    this.target = null;
    this.isFocused = false;
    this.isEnabled = false;
  }

  /**
   * @property {Object[]} fields - The assist fields defined in framework template
   * @property {String} fieldId - The id of the assist field to use
   * @property {Function} templateContext - (Optional) Helps expands template strings found in prompts
   * @property {Boolean} isFocused - Tracks if the target input is focused
   * @property {Boolean} isLoading - Indicates that a prompt is being executed
   * @property {Boolean} isEnabled - Indicates if assist is enabled in the campaign
   */
  static get properties() {
    return {
      fields: { type: Array },
      fieldId: { type: String },
      templateContext: { type: Function },

      isFocused: { state: true },
      isLoading: { state: true },
      isEnabled: { state: true },
    };
  }

  static get styles() {
    return [
      ...super.styles,
      css`
        :host {
          position: relative;
        }
        omni-tooltip.assist-icon {
          position: absolute;
          right: -0.857rem;
          top: -2.143rem;
        }
        .omni omni-tooltip.assist-icon button.button {
          font-size: 1.143rem;
        }
        omni-tooltip.assist-icon omni-icon {
          --color-icon-lines: var(--color-primary);
          fill: var(--color-primary) !important;
        }
      `,
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.dispatchNewEvent('omni-assist-enabled', {
      detail: {
        onSuccess: () => {
          this.isEnabled = true;
        },
      },
    });
  }

  /**
   * Get the prompt for the current assist field (if it exists)
   *
   * @returns {String} The assist prompt
   */
  get prompt() {
    const { prompt } =
      this.fields?.find(field => field.field_id === this.fieldId) ?? {};
    return prompt;
  }

  /**
   * Fire event to trigger executing omni-assist prompt. When a result comes back update
   * the target input field (and fires input/change events for target input).
   */
  executePrompt() {
    this.isLoading = true;
    this.dispatchNewEvent('omni-assist-execute', {
      detail: {
        prompt: this.prompt,
        templateContext: this.templateContext,
        onSuccess: result => {
          this.isLoading = false;
          this.target.value = result;
          if (['INPUT', 'TEXTAREA'].includes(this.target.nodeName)) {
            this.target.dispatchEvent(
              new CustomEvent('change', { bubbles: true }),
            );
            this.target.dispatchEvent(
              new CustomEvent('input', { bubbles: true }),
            );
          }
        },
        onError: () => {
          this.isLoading = false;
        },
      },
    });
  }

  get #assistIconTemplate() {
    if (!this.isEnabled || !this.prompt) return nothing;
    if (!this.isFocused && !this.isLoading) return nothing;

    if (this.isLoading)
      return html`
        <omni-tooltip class="assist-icon">
          <button
            slot="invoker"
            class="button is-link is-inverted is-outlined is-loading"
          >
            -
          </button>
          <div slot="content">Content Assist Loading</div>
        </omni-tooltip>
      `;

    return html`
      <omni-tooltip class="assist-icon">
        <button
          class="button is-text is-stateless"
          @mousedown=${e => {
            e.stopPropagation();
            this.executePrompt();
          }}
        >
          <omni-icon icon-id="omni:brand:omni"></omni-icon>
        </button>
        <div slot="content">Content Assist</div>
      </omni-tooltip>
    `;
  }

  render() {
    return html`
      <omni-style>
        ${this.#assistIconTemplate}
        <slot
          @slotchange=${e => {
            [this.target] = e.target
              .assignedNodes()
              .filter(node =>
                ['INPUT', 'TEXTAREA', 'CAMPAIGN-WYSIWYG'].includes(
                  node.nodeName,
                ),
              );
            this.target.addEventListener('focus', () => {
              this.isFocused = true;
            });
            this.target.addEventListener('blur', () => {
              this.isFocused = false;
            });
          }}
        ></slot>
      </omni-style>
    `;
  }
}
OmniElement.register('campaign-assist-field', CampaignAssistField);
